/* eslint-disable max-lines */
import { v4 } from 'uuid'
import { isTruthy } from '@/utils/transformers.utils'
import { META_RAW_LOCAL } from '@/config/ticketure-metadata.config'
import AdditionalInfoResponse = DataAccessInput.AdditionalInfoResponse

/**
 * Determines if the provided `item` matches the desired parameters (`timeslot`, `category`, and `tier`)
 * @param item {Transformed.LineItem} - the item to assess
 * @param timeslotId {string|undefined} - the timeslot to match. If not provided, assumed to be match
 * @param categoryId {string} - the category to match
 * @param tierId {string} - the tier to match
 */
const itemMatch = ({
  item,
  timeslotId,
  categoryId,
  tierId,
} : {
  item: Transformed.LineItem
  timeslotId: string | undefined
  categoryId: string
  tierId: string
}): boolean => (
  item.category.id === categoryId
  && item.tier.id === tierId
  && (item.timeslot ? item.timeslot.id === timeslotId : true)
)

/**
 * Retrieves the existing tickets from a specific tier based on provided parameters.
 *
 * @param {Object} params - The parameters required to fetch tickets.
 * @param {string} params.categoryId - The identifier of the category.
 * @param {string} params.tierId - The identifier of the tier.
 * @param {string} params.timeslotId - The identifier of the timeslot.
 * @param {Transformed.LineItem[]} params.localLineItems - The list of local line items to search within.
 *
 * @returns {Ticket[]} An array of tickets that match the specified tier and other parameters.
 */
export const getExistingTicketsFromTier = ({
  categoryId,
  tierId,
  timeslotId,
  localLineItems,
}: {
  categoryId: string,
  tierId: string
  timeslotId: string
  localLineItems: Transformed.LineItem[]
}): Transformed.Ticket[] => {
  let existingTickets: Transformed.Ticket[] = []
  const matching: Transformed.LineItem[] = localLineItems.filter((item: Transformed.LineItem) => itemMatch({
    item,
    timeslotId,
    categoryId,
    tierId,
  }))
  if (matching.length) {
    existingTickets = [...matching[0].tickets]
  }
  return existingTickets
}

/**
 * Given a set of {@link LineItem}s, gets the total quantity of tickets for the `currentEventId`
 * @param currentLineItems {LineItem[][]} - an array of {@link LineItem} arrays
 * @param lineItemsToDelete {LineItem[]} - an array of `LineItems` to be deleted
 * @param categoriesToExclude {string[]} - an array of {@link TicketCategory} ids to exclude when filtering line items
 * @param currentEventId {string} - the {@link Event} id corresponding to the event of interest
 */
export const getNumberOfTicketsFromLineItems = (
  currentLineItems: Transformed.LineItem[][],
  lineItemsToDelete: Transformed.LineItem[],
  categoriesToExclude: string[],
  currentEventId: string,
): number => (
  currentLineItems
    .flat()
    .filter((item) => !categoriesToExclude.includes(item.category.id))
    .filter((item) => item.event.id === currentEventId)
    .reduce((total, current) => total + current.quantity, 0)
  - lineItemsToDelete
    .filter((item) => !categoriesToExclude.includes(item.category.id))
    .filter((item) => item.event.id === currentEventId)
    .reduce((total, current) => total + current.quantity, 0)
)
/**
 * Creates a new local ticket with a temporary random ID and specified price.
 * The ID is generated using a UUID and prefixed with 'TMP-' to indicate it is temporary.
 * This ticket is not marked as in the shopping cart by default.
 *
 * @param {number} price - The price of the ticket to be set.
 * @returns {Transformed.Ticket} An object representing the ticket with a temporary ID, the specified price,
 * and a default inCart state as false.
 */
const createNewLocalTicket = (price: number): Transformed.Ticket => ({
  id: `TMP-${v4()}`, // temporary random id. The real id is return after adding to the cart
  price,
  inCart: false,
})

/**
 * Creates a local line item with the specified properties.
 *
 * @param {Object} params - The parameters for creating the line item.
 * @param {number} params.qty - The quantity of tickets in the line item.
 * @param {number} params.price - The price of a single ticket.
 * @param {Transformed.TicketCategory} params.category - The category of the ticket.
 * @param {Transformed.TicketTier} params.tier - The tier of the ticket.
 * @param {Transformed.Event} params.event - The event associated with the tickets.
 * @param {Transformed.Ticket} params.newTicket - The new ticket to include in the line item.
 * @param {Transformed.Ticket[]} params.existingTickets - The list of existing tickets.
 * @param {Transformed.Timeslot} params.timeslot - The timeslot associated with the tickets.
 * @returns {Transformed.LineItem} The created line item object containing the aggregated details.
 */
export const createLocalLineItem = ({
  qty,
  price,
  category,
  tier,
  event,
  newTicket,
  existingTickets,
  timeslot,
}: {
  qty: number
  price: number
  category: Transformed.TicketCategory
  tier: Transformed.TicketTier
  event: Transformed.Event
  newTicket: Transformed.Ticket
  existingTickets: Transformed.Ticket[]
  timeslot: Transformed.Timeslot
}): Transformed.LineItem => ({
  amount: price * qty,
  quantity: qty,
  ticketIds: [],
  timeslot,
  category,
  tier,
  event,
  tickets: [
    ...existingTickets,
    newTicket,
  ] satisfies Transformed.Ticket[],
})

/**
 * Merges a new line item with the existing collection of line items. If a line item in the
 * collection matches the specified identifiers (`categoryId`, `tierId`, and `timeslotId`),
 * it will be replaced by the new line item. If the new line item's quantity is zero, it will not
 * be added to the collection.
 *
 * @param {Object} options - The parameters for merging line items.
 * @param {Transformed.LineItem[]} options.localLineItems - The array of existing line items.
 * @param {Transformed.LineItem} options.newLineItem - The new line item to be merged.
 * @param {string} options.categoryId - The identifier for the category of the line item.
 * @param {string} options.tierId - The identifier for the tier of the line item.
 * @param {string} options.timeslotId - The identifier for the timeslot of the line item.
 * @returns {Transformed.LineItem[]} The updated array of line items after the merging operation.
 */
export const mergeNewLineItemWithExisting = ({
  localLineItems,
  newLineItem,
  categoryId,
  tierId,
  timeslotId,
}: {
  localLineItems: Transformed.LineItem[]
  newLineItem: Transformed.LineItem
  tierId: string
  categoryId: string
  timeslotId: string
}): Transformed.LineItem[] => {
  const nonMatchingLineItems = localLineItems.filter((item: Transformed.LineItem) => !(
    itemMatch({
      item,
      timeslotId,
      categoryId,
      tierId,
    })
  ))

  return newLineItem.quantity > 0 ? [...nonMatchingLineItems, newLineItem] : nonMatchingLineItems
}

/**
 * Updates the local line items by creating or modifying a new line item based on the specified parameters.
 *
 * @param {Object} params - The parameters required to create and update local line items.
 * @param {number} params.price - The price of the ticket for the line item.
 * @param {number} params.qty - The quantity of tickets being processed.
 * @param {Transformed.LineItem[]} params.localLineItems - The current list of local line items.
 * @param {Transformed.TicketCategory} params.category - The category of the ticket associated with the line item.
 * @param {Transformed.TicketTier} params.tier - The tier of the ticket associated with the line item.
 * @param {Transformed.Event} params.event - The event associated with the line item.
 * @param {Transformed.Timeslot} params.timeslot - The timeslot associated with the ticket for the line item.
 * @returns {Transformed.LineItem[]} - The updated list of local line items reflecting the addition or modification
 * of the specified line item.
 */
export const createUpdatedLocalLineItems = ({
  price, qty, localLineItems,
  category, tier, event,
  timeslot,
}: {
  price: number
  qty: number
  localLineItems: Transformed.LineItem[]
  category: Transformed.TicketCategory
  tier: Transformed.TicketTier
  event: Transformed.Event
  timeslot: Transformed.Timeslot
}): Transformed.LineItem[] => {
  const newTicket = createNewLocalTicket(price)
  const existingTickets: Transformed.Ticket[] = qty > 1
    ? getExistingTicketsFromTier({
      categoryId: category.id,
      tierId: tier.id,
      timeslotId: timeslot.id,
      localLineItems,
    })
    : []

  const newLineItem = createLocalLineItem({
    qty,
    price,
    category,
    tier,
    event,
    newTicket,
    existingTickets,
    timeslot,
  })

  return mergeNewLineItemWithExisting({
    localLineItems,
    newLineItem,
    tierId: tier.id,
    categoryId: category.id,
    timeslotId: timeslot.id,
  })
}

/**
 * Calculates the total quantity of add-ons in the given line items that match the specified tier ID and event ID.
 *
 * @param {Transformed.LineItem[]} lineItems - The list of line items to be processed.
 * @param {string} tierId - The ID of the tier to filter the line items by.
 * @param {string} thisEventId - The ID of the event to filter the line items by.
 * @returns {number} The total quantity of add-ons in the filtered line items.
 */
const getQuantityOfAddOnsInLineItemsByTier = (
  lineItems: Transformed.LineItem[],
  tierId: string,
  thisEventId: string,
): number => (
  lineItems
    .filter((lineItem) => lineItem.tier.id === tierId && lineItem.event.id === thisEventId)
    .reduce((total, item) => total + item.quantity, 0)
)

// TODO: part of this code probably needs to be refactored into a separate function
// so that we can use it to set the disabled state of the increment button
/**
 * Checks to see if there are existing add-ons for the provided eventId. If there are, and their quantity exceeds
 * the number of selected tickets, then reduce the quantity of the add-on to be equal to the number of event tickets
 * @param incomingLocalLineItems - The localLineItems updated with the modified ticket information
 * @param incomingLineItemsToDelete - The (non-local/cart) line items updated with the modified ticket information
 * @param cartLineItems - The cartLineItems before any changes have been applied
 * @param addOnsCategories - The {@link TicketCategory} object for the add-ons
 * @param event - The {@link Event} object corresponding to the event of interest
 * @param timeslot - The {@link Timeslot} of the event
 * @returns the incomingLocalLineItems and incomingLineItemsToDelete, updated with add-ons data added, if necessary
 */
const handleAddOnsQuantityLimits = (
  incomingLocalLineItems: Transformed.LineItem[],
  incomingLineItemsToDelete: Transformed.LineItem[],
  cartLineItems: readonly Transformed.LineItem[],
  addOnsCategories: Nullable<readonly Transformed.TicketCategoryWithTiers[]>,
  event: Transformed.Event,
  timeslot: Transformed.Timeslot,
):{
  updatedLocalLineItems: Transformed.LineItem[],
  updatedLineItemsToDelete: Transformed.LineItem[],
} => {
  let updatedLocalLineItems = [...incomingLocalLineItems]
  let updatedLineItemsToDelete = [...incomingLineItemsToDelete]
  const eventId = event.id

  /* istanbul ignore else */
  const addOnsCategoriesIds = addOnsCategories
    ? addOnsCategories.map((category) => category.id)
    : /* istanbul ignore next */ []

  // get all existing tiers
  const addOnTiers = incomingLocalLineItems
    .concat(cartLineItems)
    .filter((item) => addOnsCategoriesIds.includes(item.category.id) && item.event.id === eventId)
    .reduce((tierSet, addOn) => tierSet.add(addOn.tier), new Set<Transformed.TicketTier>())

  if (addOnTiers.size > 0) {
    // gets the number of existing event tickets
    const numEventTix = getNumberOfTicketsFromLineItems(
      [
        incomingLocalLineItems,
        cartLineItems.map((x) => x),
      ],
      updatedLineItemsToDelete,
      addOnsCategoriesIds,
      eventId,
    )

    addOnTiers.forEach((addOnTier) => {
      const numLocalAddOns = getQuantityOfAddOnsInLineItemsByTier(updatedLocalLineItems, addOnTier.id, eventId)
      const numCartAddOns = getQuantityOfAddOnsInLineItemsByTier(cartLineItems.map((x) => x), addOnTier.id, eventId)
      const numToDeleteAddOns = getQuantityOfAddOnsInLineItemsByTier(updatedLineItemsToDelete, addOnTier.id, eventId)

      // if > 0, too many addOns! Otherwise okay
      const addOnVsEventTixDiff = (numLocalAddOns + numCartAddOns - numToDeleteAddOns) - numEventTix
      const tooManyAddons = addOnVsEventTixDiff > 0
      const categoryForTier = addOnsCategories?.find((category) => category.id === addOnTier.categoryId)

      if (tooManyAddons && categoryForTier) {
        // Determines if there are enough local add-ons available for removal. If not, remove from cart
        const adjustment = numLocalAddOns >= addOnVsEventTixDiff ? 'local' : 'cart'
        if (adjustment === 'local') {
          updatedLocalLineItems = createUpdatedLocalLineItems({
            qty: numLocalAddOns - 1,
            price: addOnTier.price,
            category: categoryForTier,
            tier: addOnTier,
            event,
            localLineItems: updatedLocalLineItems,
            timeslot,
          })
        } else {
          updatedLineItemsToDelete = createUpdatedLocalLineItems({
            qty: numToDeleteAddOns + 1,
            price: addOnTier.price,
            category: categoryForTier,
            tier: addOnTier,
            event,
            localLineItems: updatedLineItemsToDelete,
            timeslot,
          })
        }
      }
    })
  }

  return {
    updatedLocalLineItems,
    updatedLineItemsToDelete,
  }
}

/**
 * Get qty of tickets in provided category
 * @param items {Transformed.LineItem[]|readonly LineItem[]} - array of LineItems to count
 * @param categoryId {string} - the category id to filter by
 */
export const getCategoryQty = (
  items: readonly Transformed.LineItem[],
  categoryId: string,
): number => (
  items
    .filter((item) => item.category.id === categoryId)
    .reduce((total, curr) => (
      total + curr.quantity
    ), 0)
)

/**
 * TODO: move this into transformTicketGroup
 * Checks to see if a meta data object denotes a local category
 * @param {MetaData} metaItem a meta data object
 * @returns {boolean} meta data object is local
 */
const getMetadataIsLocal = (metaItem: Transformed.MetaData): boolean => (
  metaItem.resource === 'ticket_group'
  && metaItem.metakey === META_RAW_LOCAL
  && isTruthy(metaItem.value)
)

/**
 * Is a specific category local
 * @param {TicketCategory} category ticket category
 * @returns {boolean} true if category is local
 */
export const getIsCategoryLocal = (category: Transformed.TicketCategory): boolean => (
  category.meta?.some((metaItem) => (
    metaItem && getMetadataIsLocal(metaItem)
  )) || false
)

/**
 * Get the number of tickets in the cart
 * @param {number} max
 * @returns {number}
 */
export const getMaxPerOrder = (max: Nullable<number>): number => (
  max && max > -1 ? max : -1
)

export const getIsMemberOnlyCategory = (
  category: Transformed.TicketCategory | Transformed.TicketCategoryWithTiers,
): boolean => category.visibility === 'MEMBER'

export const hasMembership = (
  membership: Nullable<Transformed.Membership> | undefined,
): boolean => !!membership?.hasMembership

/**
 * Returns the tiers a user is allowed to see for a category.
 *
 * Member-only categories use membership purchase-limit keys as the allow-list
 * for tier titles:
 * - non-members see no tiers for member-only categories
 * - members only see tiers explicitly listed in `membership.purchaseLimits`
 * - public categories return all tiers for the category
 *
 * @param {Object} args - Filtering inputs.
 * @param {Transformed.TicketCategory} args.category - The category being rendered.
 * @param {readonly Transformed.TicketTier[]} args.tiers - All tiers available in the current payload.
 * @param {Nullable<Transformed.Membership>} [args.membership] - The current user's membership.
 * @returns {Transformed.TicketTier[]} The tiers that should remain visible for the category.
 */
export const getTiersForCategory = ({
  category,
  tiers,
  membership,
}: {
  category: Transformed.TicketCategory
  tiers: readonly Transformed.TicketTier[]
  membership?: Nullable<Transformed.Membership>
}): Transformed.TicketTier[] => {
  let filteredTiers = tiers.filter((tier) => tier.categoryId === category.id)

  // Only member-only categories need entitlement filtering.
  if (!getIsMemberOnlyCategory(category)) return filteredTiers
  if (!hasMembership(membership)) return []

  // Purchase-limit keys act as the allow-list for member tier titles.
  const memberTickets = membership?.purchaseLimits
    .map((limit) => limit?.key)
    .filter((key): key is string => !!key)

  // Hide member tiers the active membership is not entitled to buy.
  filteredTiers = filteredTiers.filter((tier) => memberTickets?.includes(tier.title))

  return filteredTiers
}

/**
 * Get quantity for a given tier
 * @param {Transformed.LineItem[] | readonly LineItem[]} lineItems
 * @returns {function} function to get quantity for a given tier
 */
function getTierQty(lineItems: Transformed.LineItem[] | readonly Transformed.LineItem[]): ((args: QtyArgs) => number) {
  return ({ categoryId, tierId, timeslotId }: QtyArgs): number => (
    lineItems?.find((item) => (
      itemMatch({
        item,
        timeslotId,
        categoryId,
        tierId,
      })
    ))?.quantity || 0
  )
}

type QuantitiesForTier = {
  localQtyForTier: (args: QtyArgs) => number
  cartQtyForTier: (args: QtyArgs) => number
  toDeleteQtyForTier: (args: QtyArgs) => number
  adjustedQtyForTier: (args: QtyArgs) => number
}

export const getQuantitiesForTier = ({ localLineItems, cartLineItems, lineItemsToDelete }: {
  localLineItems: Transformed.LineItem[]
  cartLineItems: readonly Transformed.LineItem[]
  lineItemsToDelete: Transformed.LineItem[]
}): QuantitiesForTier => {
  const localQtyForTier = getTierQty(localLineItems)
  const cartQtyForTier = getTierQty(cartLineItems)
  const toDeleteQtyForTier = getTierQty(lineItemsToDelete)

  const adjustedQtyForTier = ({ categoryId, tierId, timeslotId }: QtyArgs): number => (
    localQtyForTier({ categoryId, tierId, timeslotId })
      + cartQtyForTier({ categoryId, tierId, timeslotId })
      - toDeleteQtyForTier({ categoryId, tierId, timeslotId })
  )

  return { localQtyForTier, cartQtyForTier, toDeleteQtyForTier, adjustedQtyForTier }
}

type HandleQtyClickArgs = {
  action: QtyClickAction
  tier: Transformed.TicketTier
  category: Transformed.TicketCategory | Transformed.TicketCategoryWithTiers
  localLineItems: Transformed.LineItem[]
  cartLineItems: readonly Transformed.LineItem[]
  lineItemsToDelete: Transformed.LineItem[]
  adjustedQtyForTier: (args: QtyArgs) => number
  remaining: number // remaining in timeslot
  // max per order
  maxPerOrder: number
  // max per category
  maxPerCategory: number
  timeslot: Transformed.Timeslot
  event: Transformed.Event | Transformed.EventWithTimeslotsAndMeta
  limitByCartQty?: boolean
  totalCartQty?: number
  addOnsCategories?: Nullable<readonly Transformed.TicketCategoryWithTiers[]>
}
/**
 * Based on an action that increases or decreases the number of items ordered, determines how the local line items
 * (and line items to be deleted) must be adjusted and returns `localLineItems` and `lineItemsToDelete` updated with
 * the new data. If `addOnsCategory` is provided, also adjusts line items such that they don't exceed the number
 * of event tickets
 * @param objectParameter - the information necessary to update the lineItems
 * @param objectParameter.action {{@link QtyClickAction}} whether the item is being incremented or decremented
 * @param objectParameter.tier {{@link TicketTier}} the tier of the item being adjusted
 * @param objectParameter.category {{@link TicketCategory} | {@link TicketCategoryWithTiers}} the category of the item
 * being adjusted
 * @param objectParameter.timeslot {{@link Timeslot}} the timeslot of the item being adjusted
 * @param objectParameter.localLineItems {{@link Transformed.LineItem[]}} an array of line items  to be adjusted
 * @param objectParameter.cartLineItems {{@link readonly LineItem[]}} the current line items found in the {@link Cart}
 * @param objectParameter.lineItemsToDelete {{@link Transformed.LineItem[]}} an array of the line items to be deleted
 * @param objectParameter.adjustedQtyForTier {({@link QtyArgs}) => number} a function for determining the qty per tier
 * @param objectParameter.remaining {number} the number of tickets remaining for the timeslot
 * @param objectParameter.maxPerOrder {number} the maximum number of tickets allowed for the order
 * @param objectParameter.maxPerCategory {number} the maximum number of tickets allowed for the category
 * @param objectParameter.event {{@link Event} | {@link EventWithTimeslotsAndMeta}} the event of interest
 * @param objectParameter.limitByCartQty {boolean} is the quantity limited by the cart?
 * @param objectParameter.totalCartQty {number} the total number of items in the cart
 * @param objectParameter.addOnsCategory {{@link TicketCategory} | null} The category for add-ons
 */
export const handleQtyClick = ({
  action,
  tier,
  category,
  timeslot,
  localLineItems,
  cartLineItems,
  lineItemsToDelete,
  adjustedQtyForTier,
  remaining,
  maxPerOrder,
  maxPerCategory,
  event,
  limitByCartQty = false,
  totalCartQty,
  addOnsCategories = null,
}: HandleQtyClickArgs): {
  updatedLocalLineItems: Transformed.LineItem[]
  updatedLineItemsToDelete: Transformed.LineItem[]
} => {
  // then get our stuff
  const { id: tierId, price } = tier
  const { id: categoryId } = category
  const { id: timeslotId } = timeslot

  let updatedLocalLineItems = [...localLineItems]
  let updatedLineItemsToDelete = [...lineItemsToDelete]

  // Get qty of target lineItem
  const totalQty = adjustedQtyForTier({ categoryId, tierId, timeslotId })
  const itemQty = getTierQty(localLineItems)({ categoryId, tierId, timeslotId })
  const toDeleteQty = getTierQty(lineItemsToDelete)({ categoryId, tierId, timeslotId })
  const cartQty = getTierQty(cartLineItems)({ categoryId, tierId, timeslotId })

  // need to know qty to delete to allow the increment button to be usable if
  // the user decrements tickets, then wants to add them back
  const categoryQty = getCategoryQty(cartLineItems, categoryId)
  const categoryToDeleteQty = getCategoryQty(lineItemsToDelete, categoryId)

  let newQty: number = itemQty
  let newToDeleteQty: number = toDeleteQty

  switch (action) {
  case 'increment':
    if (maxPerOrder > -1 && (totalQty >= remaining || totalQty >= maxPerOrder)) break
    if (limitByCartQty && totalCartQty && newQty > totalCartQty) break
    if (maxPerCategory > -1 && (categoryQty - categoryToDeleteQty) >= maxPerCategory) break

    // If we have items in the delete list, pull them
    // out instead of adding fresh items
    if (toDeleteQty > 0) {
      newToDeleteQty = toDeleteQty - 1
    } else {
      newQty = itemQty + 1
    }
    break
  case 'decrement':
    // If we don't have any local items and there are items
    // in the cart add an item to the delete list
    if (cartQty > 0 && itemQty === 0) {
      newToDeleteQty = toDeleteQty + 1
    } else if (itemQty > 0) {
      newQty = itemQty - 1
    }
    break
  default:
    newQty = itemQty
    newToDeleteQty = toDeleteQty
    break
  }

  updatedLocalLineItems = createUpdatedLocalLineItems({
    qty: newQty,
    price,
    category,
    tier,
    event: event as Transformed.Event,
    localLineItems,
    timeslot,
  })

  updatedLineItemsToDelete = createUpdatedLocalLineItems({
    qty: newToDeleteQty,
    price,
    category,
    tier,
    event: event as Transformed.Event,
    localLineItems: lineItemsToDelete,
    timeslot,
  })

  // Updates line items for Add-Ons if applicable,
  // otherwise returns updatedLocalLineItems and updatedLineItemsToDelete as-is
  return action === 'decrement' && addOnsCategories
    ? handleAddOnsQuantityLimits(
      updatedLocalLineItems,
      updatedLineItemsToDelete,
      cartLineItems,
      addOnsCategories,
      event as Transformed.Event,
      timeslot,
    )
    : { updatedLocalLineItems, updatedLineItemsToDelete }
}

/**
 * Do any required additional info fields not have values?
 * @param {AdditionalInfoResponse[]} additionalInfoResponses
 * @returns {boolean}
 */
export const additionalInfoMissingRequiredValue = (
  additionalInfoResponses: AdditionalInfoResponse[],
): boolean => (
  additionalInfoResponses
    .filter((value) => value.categoryId)
    .filter(({ required, value, type }) => {
      if (required) {
        if (type === 'String') {
          return !String(value).trim()
        }

        return !value
      }
      return false
    })
    .length > 0
)

/**
 * Do any of the additional info responses have bad values
 * @param {AdditionalInfoResponse[]} additionalInfoResponses
 * @returns {boolean}
 */
export const additionalInfoHasInvalidValues = (
  additionalInfoResponses: AdditionalInfoResponse[],
): boolean => (
  additionalInfoResponses.filter(({ type, value }) => {
    const valueAsString = String(value).trim()
    return type === 'Int'
      // does the string contain 0 or more numbers
      ? !/^\d*$/.test(valueAsString)
      : false
  }).length > 0
)

/**
 * Check to see that all required additional info fields have been
 * filled out and that all responses are valid
 * @param {Transformed.LineItem[]} localLineItems local line items
 * @param {DataAccessInput.AdditionalInfoResponse[]} additionalInfoResponses
 * @returns {boolean}
 */
export const addToCartBlocked = (
  localLineItems: Transformed.LineItem[],
  additionalInfoResponses: DataAccessInput.AdditionalInfoResponse[],
): boolean => {
  const categoryIds = localLineItems.map((lineItem) => lineItem.category.id)
  const filteredAdditionalInfo = additionalInfoResponses.filter((item) => categoryIds.includes(item.categoryId))
  return additionalInfoMissingRequiredValue(filteredAdditionalInfo)
    || additionalInfoHasInvalidValues(filteredAdditionalInfo)
}

/**
 * Transform additional info responses for the Ticketure cart API.
 * Filters to only categories present in localLineItems, removes empty/invalid responses,
 * and formats as { ticket_group_id, data: { key: value } } per category.
 */
export const transformAdditionalInfoForCart = (
  additionalInfoResponses: DataAccessInput.AdditionalInfoResponse[],
  localLineItems: Transformed.LineItem[],
): Array<{ ticket_group_id: string, data: Record<string, string | boolean | number> }> | null => {
  const categoryIds = [...new Set(localLineItems.map((lineItem) => lineItem.category.id))]

  const filtered = additionalInfoResponses
    .filter(({ categoryId }) => categoryIds.includes(categoryId))
    .filter(({ value, required, type }) => {
      if (type === 'String' && String(value).trim() === '') return false
      if (required && type === 'Boolean' && value === false) return false
      // Remove empty select if the select is not required
      if (!required && type === 'Select' && String(value).trim() === '') return false
      return true
    })

  const response = categoryIds.map((categoryId) => ({
    // eslint-disable-next-line camelcase
    ticket_group_id: categoryId,
    data: Object.fromEntries(
      filtered
        .filter(({ categoryId: cId }) => cId === categoryId)
        .map(({ key, value, type }) => {
          if (type === 'Boolean') return [key, value === true || value === 'true']
          if (type === 'Int') return [key, typeof value === 'number' ? value : parseInt(String(value), 10)]
          return [key, String(value)]
        }),
    ),
  }))
    .filter(({ data }) => Object.keys(data).length > 0)

  return response.length > 0 ? response : null
}

/**
 * Is a specific category local
 * @param {Cart} cart category
 * @returns {boolean} true if local discounted tickets are in cart
 */
export const localTicketsInCart = (cart: Transformed.Cart): boolean => cart.lineItems
  .filter((el) => getIsCategoryLocal(el.category)).length > 0

/**
 * Determines whether the specified limits have been reached for a given tier, category, or membership ticket.
 *
 * @param {Object} params - Parameters for evaluating the limits.
 * @param {number} params.quantityForTier - The current quantity for the specified tier.
 * @param {number} params.quantityForCategory - The current quantity for the specified category.
 * @param {number} params.remainingQuantity - The remaining quantity available for the tier.
 * @param {number} params.maxLimitForTier - The maximum allowable quantity for the tier. A value of -1 indicates no limit.
 * @param {number} params.maxLimitForCategory - The maximum allowable quantity for the category. A value of -1 indicates no limit.
 * @param {number|undefined} params.membershipTicketLimit - The maximum allowable quantity for membership tickets, if defined.
 * @param {Array<Object>} [params.quantitiesAndLimits] - An optional array containing quantities and their respective limits.
 *
 * @returns {boolean} Returns `true` if any of the following conditions are met:
 * - The quantity for the tier exceeds or equals the `membershipTicketLimit` (if defined).
 * - The quantity for the tier exceeds or equals the `maxLimitForTier` (if a tier limit exists).
 * - The quantity for the category exceeds or equals the `maxLimitForCategory` (if a category limit exists).
 * - The quantity for the tier matches the `remainingQuantity`.
 * Otherwise, returns `false`.
 */
export const getHasReachedLimit = ({
  quantityForTier,
  remainingQuantity,
  maxLimitForTier,
  maxLimitForCategory,
  quantityForCategory,
  membershipTicketLimit,
} : {
  quantityForTier: number
  quantityForCategory: number
  remainingQuantity: number
  maxLimitForTier: number
  maxLimitForCategory: number
  membershipTicketLimit: number | undefined
  quantitiesAndLimits?: Array<{ quantity: number, limit: number }>
}): boolean => {
  // if a membership ticket limit exists, don't allow quantity to exceed ticket limit
  if (membershipTicketLimit) {
    return quantityForTier >= membershipTicketLimit
  }

  // check to see if we have reached our limit at the tier level
  if (maxLimitForTier > -1 && quantityForTier >= maxLimitForTier) {
    return true
  }

  // check to see if we have reached our limit at the category level
  if (maxLimitForCategory > -1 && quantityForCategory >= maxLimitForCategory) {
    return true
  }

  // check to see if we are exceeding the number of tickets for sale
  return quantityForTier === remainingQuantity
}

/**
 * Finds and returns the smallest non-negative integer from the given array.
 * A non-negative integer is defined as a number greater than or equal to 0
 * and without fractional parts.
 *
 * In this case we are looking at capacity numbers and -1 indicates Infinity
 *
 * @param {number[]} numbers - The array of numbers to search through.
 * @returns {number} The smallest non-negative integer in the array.
 */
export const getSmallestNonNegativeInteger = (numbers: number[]): number => {
  const isNonNegativeInteger = (number: number): boolean => (
    number >= 0 && Number.isInteger(number)
  )

  const nonNegativeIntegers = numbers.filter(isNonNegativeInteger)

  return Math.min(...nonNegativeIntegers)
}

/**
 * Calculates the remaining amount for a specific ticket category by determining the smallest positive integer
 * between the provided values: `remainingForTimeslot` and `category.capacity.remaining`.
 *
 * @param {number} remainingForTimeslot - The remaining capacity available for the current timeslot.
 * @param {Transformed.TicketCategory | Transformed.TicketCategoryWithTiers} category - The ticket category object
 * containing remaining capacity information.
 * @returns {number} The smallest positive integer representing the remaining capacity for the category.
 */
export const getRemainingForCategory = (
  remainingForTimeslot: number,
  category: Transformed.TicketCategory | Transformed.TicketCategoryWithTiers,
): number => {
  return getSmallestNonNegativeInteger([remainingForTimeslot, category.capacity.remaining])
}
